import { Badge } from 'components/ui/badge';
import { CSSProperties, ReactNode } from 'react';
import { cn } from 'utils/cn';

/** A square tile holding one glyph. */
const TILE = 'size-6 rounded-sm px-0 py-0';
const GLYPH_TILE = cn(TILE, 'text-fg bg-grey-200 [&_svg]:size-4');

const TYPE = {
    contactRelationship: 'bg-primary/10 text-primary rounded-full',
    partyType: GLYPH_TILE,
    experienceRecord: GLYPH_TILE,
    // The glyph is sized by the caller, so the tile does not resize it.
    personaNonGrata: cn(TILE, 'bg-warning-light text-secondary'),
    // The opponent marker's two states share one footprint: the filled one keeps a transparent border as
    // wide as the hollow one's outline, so a state change never moves the row.
    opponentOngoing: cn(TILE, 'bg-warning-light text-warning-darker border-transparent'),
    opponentCompleted: cn(TILE, 'text-grey-600 border-grey-600 bg-transparent'),
    statusWarning:
        'bg-warning-lighter text-warning border-warning-lighter h-[22px] rounded-sm px-2 py-px text-xs leading-5 font-normal',
} as const;

type ChipType = keyof typeof TYPE;

interface Props {
    label: ReactNode;
    type?: ChipType;
    isError?: boolean;
    /** Chips render at one size. */
    size?: 'small';
    className?: string;
    style?: CSSProperties;
}

export default function Chip({ label, type = 'contactRelationship', isError, className, style }: Props) {
    return (
        <Badge
            variant="outline"
            className={cn(
                'border-transparent font-medium',
                TYPE[type],
                isError && 'bg-error-light text-error',
                className
            )}
            style={style}
        >
            {/* Wrapped, so the badge's own rule for a bare child SVG does not resize the label's glyph. */}
            <span className="inline-flex items-center">{label}</span>
        </Badge>
    );
}
